import { ITEMS, type ItemId } from '@/data/items';

/**
 * What the cart buys and sells.
 *
 * A travelling merchant rather than a shop in a town, because there is no town
 * yet. The cart is parked on the farm, which also means the first money in the
 * game is earned within sight of the crops that made it.
 *
 * Prices are derived from `sellPrice` rather than listed twice. A second table
 * of numbers to keep in step with the first is how an item ends up worth more
 * to buy than it is to make.
 */

/** What the cart pays, as a fraction of an item's worth. */
export const SELL_RATE = 1;

/**
 * And what it charges. Above 1 on purpose: the margin is the merchant's, and
 * without it buying and selling the same item in a loop would print money.
 */
export const BUY_MARKUP = 2.5;

/** Everything the cart will part with. */
export const SHOP_STOCK: readonly ItemId[] = [
  'seed:turnip',
  'seed:potato',
  'seed:tomato',
  'seed:pumpkin',
  'feed',
  'bait:worm',
];

export function sellValue(item: ItemId): number {
  return Math.max(1, Math.round((ITEMS[item].sellPrice ?? 0) * SELL_RATE));
}

/**
 * @param discount Fraction off, earned by friendship. Nothing is ever free:
 * the floor of two survives any discount, so knowing a merchant well is worth
 * something without turning his cart into a giveaway.
 */
export function buyPrice(item: ItemId, discount = 0): number {
  const base = ITEMS[item].sellPrice ?? 0;
  const full = base * BUY_MARKUP * (1 - Math.min(0.9, Math.max(0, discount)));
  return Math.max(2, Math.round(full));
}

/** True for anything the cart is willing to take off your hands. */
export function isSellable(item: ItemId): boolean {
  return (ITEMS[item].sellPrice ?? 0) > 0;
}
